import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, X } from 'lucide-react';

import { cancelConvite, type EscritorioConvite } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { RoleBadge } from './papeis';

interface ConvitesAbaProps {
  escritorioId: string;
  convites: EscritorioConvite[];
  onConvidar: () => void;
}

/** Convites ainda não aceitos: quem, com que papel, até quando; cancelar ou copiar o link. */
export function ConvitesAba({ escritorioId, convites, onConvidar }: ConvitesAbaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const pendentes = convites.filter((c) => c.status === 'PENDENTE');

  const cancelar = useMutation({
    mutationFn: (convite: EscritorioConvite) => cancelConvite(escritorioId, convite.id),
    onSuccess: () => {
      showSuccess('Convite cancelado');
      queryClient.invalidateQueries({
        queryKey: queryKeys.escritorios.convites(escritorioId),
      });
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (pendentes.length === 0) {
    return (
      <EmptyState
        icon={<Mail />}
        title="Nenhum convite pendente"
        description="Os convites enviados aparecem aqui até serem aceitos."
        action={{ label: 'Convidar', onClick: onConvidar }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={onConvidar}>
          <Plus className="mr-2 h-4 w-4" />
          Convidar
        </Button>
      </div>
      <Card className="divide-y">
        {pendentes.map((c) => (
          <div key={c.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="[overflow-wrap:anywhere] text-sm font-medium">{c.email}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <RoleBadge role={c.role} />
                <span>Convidado por {c.convidado_por_nome}</span>
                <span>· vale até {formatDate(c.expires_at)}</span>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1 self-end sm:self-auto">
              {c.url_convite && <CopyButton text={c.url_convite} label="Copiar link do convite" />}
              <Button
                variant="ghost"
                size="sm"
                className="h-11 text-muted-foreground hover:text-destructive sm:h-8"
                onClick={() => cancelar.mutate(c)}
                disabled={cancelar.isPending && cancelar.variables?.id === c.id}
              >
                {cancelar.isPending && cancelar.variables?.id === c.id ? (
                  <Spinner size={16} className="mr-1 text-inherit" />
                ) : (
                  <X className="mr-1 h-4 w-4" />
                )}
                Cancelar
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
